import React from 'react';
import { observer } from 'mobx-react-lite';

import { FormControl, MenuItem, Select, Switch } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import UserAndGroupPicker, { PickerValue } from 'components/SharedFormComponents/UserAndGroupPicker';
import { PickerItemKind } from 'components/SharedFormComponents/UserAndGroupPicker/types';
import Logger from 'utils/logger';
import API from 'utils/api/api';
import { useStore } from 'utils/hooks/store';
import { env } from 'config';
import { UserRole } from 'core/constants';
import { DocumentAccessLevel, DocumentPermissionKind } from 'utils/api/modules/entities/entities.types';

import PeopleWithAccessList from './PeopleWithAccessList';
import GeneralAccessSection from './GeneralAccessSection';
import GroupLicenceCoverageNotice from './GroupLicenceCoverageNotice';
import TransferOwnershipModal from './TransferOwnershipModal';
import {
  ModalDescription,
  GeneralAccessRow,
  GeneralAccessInfo,
  GeneralAccessIcon,
  GeneralAccessLabel,
  GeneralAccessTitle,
  GeneralAccessHint,
  ShareLinkUrl,
  CopyLinkRow,
  AddPeopleRow,
} from './styles';

interface ShareDocumentModalProps {
  open: boolean;
  entityId: string;
  onClose: () => void;
}

const ShareDocumentModal: React.FC<ShareDocumentModalProps> = observer(({ open, entityId, onClose }) => {
  const { documentsDetails, notification, session } = useStore();
  const { isOwner, details } = documentsDetails;

  const isAdmin = session.user?.role === UserRole.Admin;
  const isManager = session.user?.role === UserRole.Manager || session.user?.role === UserRole.AccountManager;
  const canManage = isOwner || isAdmin || isManager;
  const groupsEnabled = !!session.user?.groupsEnabled;

  // Share link state (immediate, not batched)
  const [shareLink, setShareLink] = React.useState<{
    uuid: string;
    isActive: boolean;
    link: string;
  } | null>(null);
  const [shareLinkLoading, setShareLinkLoading] = React.useState(false);

  const [transferOpen, setTransferOpen] = React.useState(false);

  const [inviteLevel, setInviteLevel] = React.useState<DocumentAccessLevel>(DocumentAccessLevel.View);

  const ownerUuid = details?.owner?.uuid ?? details?.createdBy?.uuid;

  const checkedOptions: PickerValue[] = [
    ...(ownerUuid ? [{ kind: PickerItemKind.User as const, uuid: ownerUuid, name: '' }] : []),
    ...documentsDetails.displayPermissions.map(
      (p): PickerValue =>
        p.kind === DocumentPermissionKind.Group
          ? { kind: PickerItemKind.Group, uuid: p.group.uuid, name: p.group.name, memberCount: p.group.memberCount }
          : { kind: PickerItemKind.User, uuid: p.user.uuid, name: p.user.name }
    ),
  ];

  const handlePickerChange = (next: PickerValue[]): void => {
    const checkedUuidsByKind = new Set(checkedOptions.map((c) => `${c.kind}-${c.uuid}`));

    next
      .filter((item) => !checkedUuidsByKind.has(`${item.kind}-${item.uuid}`))
      .forEach((item) => documentsDetails.addDraftPermission(item, inviteLevel));
  };

  const handleUncheckOption = (option: PickerValue): void => {
    const row = documentsDetails.displayPermissions.find(
      (p) =>
        (p.kind === DocumentPermissionKind.Group &&
          option.kind === PickerItemKind.Group &&
          p.group.uuid === option.uuid) ||
        (p.kind === DocumentPermissionKind.User && option.kind === PickerItemKind.User && p.user.uuid === option.uuid)
    );
    if (row) documentsDetails.removeDraftPermission(row.uuid);
  };

  React.useEffect(() => {
    if (!open) return;

    documentsDetails.resetDraft();
    documentsDetails.loadPermissions();
    if (canManage) loadShareLink();
  }, [open, entityId]);

  const loadShareLink = async (): Promise<void> => {
    setShareLinkLoading(true);
    try {
      const { data } = await API.entities.loadShareDocumentLink(entityId)();
      if (data.length > 0) {
        setShareLink({
          uuid: data[0].uuid,
          isActive: data[0].isActive,
          link: `${env.REACT_APP_PUBLIC_FORM_URL}/shared/documents/${entityId}/${data[0].uuid}`,
        });
      }
    } catch (e) {
      Logger.error('Failed to load share link', e);
    } finally {
      setShareLinkLoading(false);
    }
  };

  const handleDone = async (): Promise<void> => {
    const success = await documentsDetails.saveDraft();
    if (success) onClose();
  };

  const handleToggleShareLink = async (checked: boolean): Promise<void> => {
    if (!shareLink) {
      try {
        const { data } = await API.entities.shareDocumentLink(entityId)();
        const link = `${env.REACT_APP_PUBLIC_FORM_URL}/shared/documents/${entityId}/${data.uuid}`;
        setShareLink({ uuid: data.uuid, isActive: checked, link });
        await API.entities.toggleShareDocumentLink(entityId, data.uuid, checked)();
      } catch (e) {
        Logger.error('Failed to create share link', e);
      }
      return;
    }

    try {
      await API.entities.toggleShareDocumentLink(entityId, shareLink.uuid, checked)();
      setShareLink({ ...shareLink, isActive: checked });
    } catch (e) {
      Logger.error('Failed to toggle share link', e);
      notification.enqueueErrorSnackbar('Failed to toggle share link');
    }
  };

  const handleCopyLink = (): void => {
    const link = shareLink?.isActive ? shareLink.link : `${window.location.origin}/documents/${entityId}`;
    navigator.clipboard
      .writeText(link)
      .then(() => notification.enqueueSuccessSnackbar('Link copied to clipboard'))
      .catch(() => notification.enqueueErrorSnackbar('Failed to copy link'));
  };

  return (
    <>
      <CustomModal openModal={open} modalTitle="Share document" onClose={onClose} modalWidth="sm">
        <ModalDescription>
          {canManage
            ? 'Control who can view and edit this document. Changes apply when you click Save changes.'
            : 'See who has access to this document.'}
        </ModalDescription>

        {canManage && (
          <AddPeopleRow>
            <UserAndGroupPicker
              id="add-document-permission"
              value={[]}
              onChange={handlePickerChange}
              checkedOptions={checkedOptions}
              onUncheckOption={handleUncheckOption}
              enableGroups={groupsEnabled}
              licenses="documents"
              placeholder="Search users..."
              groupsPlaceholder="Add users or groups"
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={inviteLevel} onChange={(e) => setInviteLevel(e.target.value as DocumentAccessLevel)}>
                <MenuItem value={DocumentAccessLevel.View}>Can view</MenuItem>
                <MenuItem value={DocumentAccessLevel.Edit}>Can edit</MenuItem>
              </Select>
            </FormControl>
          </AddPeopleRow>
        )}

        <PeopleWithAccessList
          owner={details?.owner ?? details?.createdBy ?? null}
          permissions={documentsDetails.displayPermissions}
          isOwnerOrAdmin={canManage}
          onUpdate={documentsDetails.updateDraftPermission}
          onRemove={documentsDetails.removeDraftPermission}
          onTransferOwnership={() => setTransferOpen(true)}
        />

        {canManage && (
          <GroupLicenceCoverageNotice
            entityId={entityId}
            groupUuids={documentsDetails.pendingGroupUuids}
            savedGroupUuids={documentsDetails.savedGroupUuids}
            groupNames={documentsDetails.displayPermissions
              .filter((p) => p.kind === DocumentPermissionKind.Group)
              .map((p) => p.group.name)}
          />
        )}

        <GeneralAccessSection
          defaultAccess={documentsDetails.draftDefaultAccess}
          isOwnerOrAdmin={canManage}
          onUpdate={async (newAccess) => {
            documentsDetails.setDraftDefaultAccess(newAccess);
            return true;
          }}
        />

        {canManage && (
          <>
            <GeneralAccessRow>
              <GeneralAccessInfo>
                <GeneralAccessIcon>
                  <LinkIcon sx={{ fontSize: 20 }} />
                </GeneralAccessIcon>
                <GeneralAccessLabel>
                  <GeneralAccessTitle>Public share link</GeneralAccessTitle>
                  <GeneralAccessHint>
                    {shareLink?.isActive
                      ? 'Anyone with the link can view (read-only)'
                      : 'Enable to share outside your organisation'}
                  </GeneralAccessHint>
                </GeneralAccessLabel>
              </GeneralAccessInfo>
              {shareLinkLoading ? (
                <CenteredCircularProgress />
              ) : (
                <Switch
                  checked={shareLink?.isActive ?? false}
                  onChange={(_, checked) => handleToggleShareLink(checked)}
                  size="small"
                />
              )}
            </GeneralAccessRow>
            {shareLink?.isActive && <ShareLinkUrl>{shareLink.link}</ShareLinkUrl>}
          </>
        )}

        <CopyLinkRow>
          <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyLink}>
            Copy link
          </Button>
          {canManage && (
            <Button variant="contained" size="small" onClick={handleDone} disabled={documentsDetails.saving}>
              {documentsDetails.saving ? 'Saving...' : 'Save changes'}
            </Button>
          )}
        </CopyLinkRow>
      </CustomModal>

      <TransferOwnershipModal open={transferOpen} onClose={() => setTransferOpen(false)} />
    </>
  );
});

export default ShareDocumentModal;
